import {MipLevelRegion, Regions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {HalImage} from "@cm/hal"
import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {CreateDataArgs, ImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {definition} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/generate-weight-mask/definition"
import {Vector2} from "@cm/math"
import {HalPainterImageCompositorRef} from "@cm/hal/hal-ref"
import {ImageOpHalParameters, UpdateImageOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"
import {AnyImageOpHalData, CtorArgs, ImageOpHalData} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-data"
import {UPDATE_COMPLEXITY_MID} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/update-complexity"

type Definition = typeof definition

export class ImageOpTilingGenerateWeightMaskHal extends ImageOpHal<Definition, ImageOpTilingGenerateWeightMaskHalData> {
    constructor(ctx: ImageOpContextHal, parameters: ImageOpHalParameters<Definition>) {
        super(ctx)
        this.setParameters(parameters)
    }

    protected override createData(args: CreateDataArgs<Definition, ImageOpTilingGenerateWeightMaskHalData>): ImageOpTilingGenerateWeightMaskHalData {
        return new ImageOpTilingGenerateWeightMaskHalData({
            ...args,
            descriptor: {
                width: args.parameters.region.width,
                height: args.parameters.region.height,
                channels: 1,
            },
            outputImageReuse: "reuse-if-same-descriptor",
            updateComplexity: UPDATE_COMPLEXITY_MID,
        })
    }
}

class ImageOpTilingGenerateWeightMaskHalData extends ImageOpHalData<Definition> {
    constructor(args: CtorArgs<Definition, ImageOpTilingGenerateWeightMaskHalData>) {
        super(args)
        this.compositor =
            args.prevData?.compositor.addRef() ??
            args.ctx.createPainter(
                "compositor",
                `
            uniform vec2 u_sourceImageSize;
            uniform ivec2 u_offset;
            
            vec4 computeColor(ivec2 targetPixel) {
                vec2 sourcePixel = vec2(targetPixel + u_offset);
                float coverage = 1.0;
                if (sourcePixel.x < 0.0) {
                    coverage *= max(0.0, 1.0 + sourcePixel.x);
                } else if (sourcePixel.x >= u_sourceImageSize.x - 1.0) {
                    coverage *= max(0.0, 1.0 - (sourcePixel.x - (u_sourceImageSize.x - 1.0)));
                }
                if (sourcePixel.y < 0.0) {
                    coverage *= max(0.0, 1.0 + sourcePixel.y);
                } else if (sourcePixel.y >= u_sourceImageSize.y - 1.0) {
                    coverage *= max(0.0, 1.0 - (sourcePixel.y - (u_sourceImageSize.y - 1.0)));
                }
                return vec4(coverage, coverage, coverage, coverage);
            }
        `,
            )
    }

    protected override dispose() {
        this.compositor.release()
        super.dispose()
    }

    protected async writeRegion(outputImage: HalImage, mipLevelRegion: MipLevelRegion, _options?: UpdateImageOptions): Promise<void> {
        const mipLevelScale = 1 << (mipLevelRegion.mipLevel + this.parameters.mipLevelOffset)
        const sourceImageMipLevelSize = Vector2.fromSize2Like(this.parameters.sourceImage.descriptor).divInPlace(mipLevelScale)
        this.compositor.ref.paint({
            target: outputImage,
            parameters: {
                u_sourceImageSize: {type: "float2", value: sourceImageMipLevelSize},
                u_offset: {type: "int2", value: Vector2.fromVector2Like(this.parameters.region)},
            },
            options: {
                targetRegion: mipLevelRegion,
                targetMipLevel: mipLevelRegion.mipLevel,
            },
        })
    }

    protected override onSourceRegionsInvalidated(_sourceImage: AnyImageOpHalData, _sourceRegions: Regions) {}

    readonly compositor: HalPainterImageCompositorRef
}
